import type { SxProps } from '@mui/material';
import {
  Box,
  Chip,
  LinearProgress,
  ListItemButton,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '@/shared/lib';
import { getSavedPresetURL } from '../../libs/getSavedPresetURL';
import type { SavedPresetRecord } from '../../model/types';
import { FavoritePresetIcon } from '../FavoritePresetIcon/FavoritePresetIcon';

interface Props {
  sx?: SxProps;
  presetRecord: SavedPresetRecord;
  viewURL?: string;
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
  onClick?: (preset: SavedPresetRecord) => void;
}

export function SavedPresetItem({
  sx,
  presetRecord,
  viewURL,
  leftSlot,
  rightSlot,
  onClick,
}: Props) {
  return (
    <Box key={presetRecord.id} component="li">
      <ListItemButton
        sx={{
          py: 1,
          pl: 2,
          pr: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          width: '100%',
          position: 'relative',

          '&:hover': {
            backgroundColor: '#E7ECFA',

            '& .MuiChip-root': {
              backgroundColor: 'white',
            },
          },

          ...sx,
        }}
        onClick={() => {
          onClick?.(presetRecord);
        }}
      >
        {leftSlot}
        <Box
          component={Link}
          to={getSavedPresetURL({
            baseURL: viewURL,
            presetId: presetRecord.id,
          })}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            textDecoration: 'none',
            width: '100%',
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              color: '#444444',
              fontWeight: 400,
              fontSize: '0.875rem',
              lineHeight: '1.375rem',
              letterSpacing: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
            title={presetRecord.name}
          >
            {presetRecord.name}
            {presetRecord.isFavorite && <FavoritePresetIcon />}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '0.75rem',
              lineHeight: '1.375rem',
              letterSpacing: 0,
              color: '#817E7E',
            }}
          >
            Last modified:{' '}
            <span title={new Date(presetRecord.updatedAt).toLocaleString()}>
              {formatDate(presetRecord.updatedAt)}
            </span>
          </Typography>
        </Box>
        {presetRecord.isDefault && (
          <Chip
            label="Default"
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              lineHeight: '0.875rem',
              letterSpacing: 0,
              height: 22,
              backgroundColor: '#E7ECFA',

              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
        )}
        {rightSlot}
        {presetRecord.updating && (
          <LinearProgress
            color="primary"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#DDDFE7',
              height: 2,
            }}
          />
        )}
      </ListItemButton>
    </Box>
  );
}
